/**
 * FieldSaveQueue - Per-field serial save queue with coalescing and retry.
 *
 * Each field gets its own independent queue. Operations execute serially (one at
 * a time per field) in FIFO order. Consecutive update operations for the same
 * field are coalesced - only the latest payload is sent. Create and delete
 * operations are never coalesced.
 *
 * This class has zero Vue dependencies and no reactivity coupling.
 */

const DEFAULT_OPTIONS = {
  debounceMs: 500,
  maxRetries: 3,
  retryBaseDelayMs: 1000,
  retryMaxDelayMs: 8000,
  slowThresholdMs: 4000,
};

function createQueue() {
  return {
    status: 'idle',
    inFlight: null,       // { type: 'create'|'update'|'delete', data, retryCount }
    pending: null,        // { type: 'create'|'update'|'delete', data }
    debounceTimer: null,
    slowTimer: null,
    retryTimer: null,
    lastError: null,
  };
}

export class FieldSaveQueue {
  /**
   * @param {Object} apiAdapter - { create(data), update(id, data), delete(id) }
   * @param {Object} options
   * @param {number}   options.debounceMs          - Debounce window for update coalescing (default: 500)
   * @param {number}   options.maxRetries          - Max retry attempts per operation (default: 3)
   * @param {number}   options.retryBaseDelayMs    - Base delay for exponential backoff (default: 1000)
   * @param {number}   options.retryMaxDelayMs     - Max backoff delay cap (default: 8000)
   * @param {number}   options.slowThresholdMs     - Time after which slow warning fires (default: 4000)
   * @param {Function} options.onStatusChange      - (fieldId, status) => void
   * @param {Function} options.onIdSwap            - (tempId, serverId) => void
   * @param {Function} options.onPermanentFailure  - (fieldId, operation, error) => void
   */
  constructor(apiAdapter, options = {}) {
    this._apiAdapter = apiAdapter;
    this._options = { ...DEFAULT_OPTIONS, ...options };
    this._queues = new Map();
    this._idMap = new Map();
    this._destroyed = false;
    this._slowFields = new Set();
  }

  // ── Public API ──────────────────────────────────────────────

  /**
   * Enqueue a create operation. Executes immediately (not debounced, not coalesced).
   * @param {string} tempId - Client-generated UUID for the new field
   * @param {Object} data   - Payload for apiAdapter.create()
   */
  enqueueCreate(tempId, data) {
    const q = this._getOrCreateQueue(tempId);
    q.pending = { type: 'create', data };
    this._clearDebounce(q);
    this._processNext(tempId);
  }

  /**
   * Enqueue an update operation. Debounced per-field; coalesced with any
   * pending update already waiting in the queue.
   * @param {string} fieldId - Field ID (may be temp or server ID)
   * @param {Object} data    - Payload for apiAdapter.update()
   */
  enqueueUpdate(fieldId, data) {
    const canonicalId = this._resolveId(fieldId);
    const q = this._getOrCreateQueue(canonicalId);

    // Coalesce: if a pending update exists, merge the new data into it
    if (q.pending && q.pending.type === 'update') {
      q.pending.data = { ...q.pending.data, ...data };
    } else if (!q.pending) {
      q.pending = { type: 'update', data };
    }
    // If pending is a create or delete, don't overwrite it - the update
    // will be enqueued after the create/delete completes next time around.

    this._setStatus(canonicalId, q.inFlight ? 'queued' : 'debouncing');

    // Restart debounce timer
    this._clearDebounce(q);
    q.debounceTimer = setTimeout(() => {
      q.debounceTimer = null;
      this._processNext(canonicalId);
    }, this._options.debounceMs);
  }

  /**
   * Enqueue a delete operation. Executes immediately.
   * Cancels any pending updates for this field (delete wins).
   * @param {string} fieldId - Field ID
   */
  enqueueDelete(fieldId) {
    const canonicalId = this._resolveId(fieldId);
    const q = this._getOrCreateQueue(canonicalId);

    // Delete wins over any pending update
    q.pending = { type: 'delete', data: null };
    this._clearDebounce(q);
    this._clearRetry(q);
    this._processNext(canonicalId);
  }

  /**
   * Get the current status for a field.
   * @param {string} fieldId
   * @returns {'idle'|'debouncing'|'saving'|'queued'|'error'}
   */
  getStatus(fieldId) {
    const canonicalId = this._resolveId(fieldId);
    const q = this._queues.get(canonicalId);
    return q ? q.status : 'idle';
  }

  /**
   * True if any field has been in 'saving' state longer than slowThresholdMs.
   * @returns {boolean}
   */
  get isSlowWarningActive() {
    return this._slowFields.size > 0;
  }

  /**
   * Resolve a field ID (handles temp→server ID mapping).
   * @param {string} id
   * @returns {string}
   */
  resolveId(id) {
    return this._resolveId(id);
  }

  /**
   * Update the API adapter reference (e.g. when parent computed re-evaluates).
   * @param {Object} adapter
   */
  setApiAdapter(adapter) {
    this._apiAdapter = adapter;
  }

  /**
   * Cancel all pending operations and timers.
   */
  destroy() {
    this._destroyed = true;
    for (const [, q] of this._queues) {
      this._clearDebounce(q);
      this._clearSlow(q);
      this._clearRetry(q);
    }
    this._queues.clear();
    this._idMap.clear();
    this._slowFields.clear();
  }

  // ── Internal ────────────────────────────────────────────────

  _resolveId(id) {
    return this._idMap.get(id) || id;
  }

  _getOrCreateQueue(fieldId) {
    if (!this._queues.has(fieldId)) {
      this._queues.set(fieldId, createQueue());
    }
    return this._queues.get(fieldId);
  }

  _setStatus(fieldId, status) {
    const q = this._queues.get(fieldId);
    if (!q || q.status === status) return;
    q.status = status;
    if (status !== 'saving') {
      this._slowFields.delete(fieldId);
    }
    this._options.onStatusChange?.(fieldId, status);
  }

  _clearDebounce(q) {
    if (q.debounceTimer) {
      clearTimeout(q.debounceTimer);
      q.debounceTimer = null;
    }
  }

  _clearSlow(q) {
    if (q.slowTimer) {
      clearTimeout(q.slowTimer);
      q.slowTimer = null;
    }
  }

  _clearRetry(q) {
    if (q.retryTimer) {
      clearTimeout(q.retryTimer);
      q.retryTimer = null;
    }
  }

  async _processNext(fieldId) {
    if (this._destroyed) return;

    const q = this._queues.get(fieldId);
    if (!q || q.inFlight || !q.pending) return;

    // Move pending to in-flight
    q.inFlight = { ...q.pending, retryCount: 0 };
    q.pending = null;
    this._setStatus(fieldId, 'saving');

    // Start slow-warning timer
    q.slowTimer = setTimeout(() => {
      if (this._destroyed) return;
      this._slowFields.add(fieldId);
      this._options.onStatusChange?.(fieldId, 'saving'); // re-fire so caller can re-check isSlowWarningActive
    }, this._options.slowThresholdMs);

    await this._executeWithRetry(fieldId);
  }

  async _executeWithRetry(fieldId) {
    if (this._destroyed) return;

    const q = this._queues.get(fieldId);
    if (!q || !q.inFlight) return;

    try {
      await this._executeOperation(fieldId, q.inFlight);

      if (this._destroyed) return;

      // _executeOperation may have re-keyed the queue from tempId to serverId
      // (after a successful create that returned a server-assigned ID). Resolve
      // to the CANONICAL id for status updates - otherwise _setStatus(tempId,
      // 'idle') would find no queue under the old tempId and silently no-op,
      // leaving the status stuck at 'saving' and the orange "saving" dot lit
      // forever on the field's overlay (until a subsequent update kicks off
      // a fresh save cycle that happens to flip the status).
      const canonicalId = this._resolveId(fieldId);

      // Success
      this._clearSlow(q);
      this._slowFields.delete(fieldId);
      this._slowFields.delete(canonicalId);
      q.inFlight = null;
      q.lastError = null;

      if (q.pending) {
        this._setStatus(canonicalId, 'queued');
        this._processNext(canonicalId);
      } else {
        this._setStatus(canonicalId, 'idle');
      }
    } catch (err) {
      if (this._destroyed) return;

      this._clearSlow(q);
      this._slowFields.delete(fieldId);

      const retryable = this._isRetryable(err);
      if (retryable && q.inFlight.retryCount < this._options.maxRetries) {
        q.inFlight.retryCount++;
        const delay = Math.min(
          this._options.retryBaseDelayMs * Math.pow(2, q.inFlight.retryCount - 1),
          this._options.retryMaxDelayMs
        );
        q.retryTimer = setTimeout(() => {
          q.retryTimer = null;
          this._executeWithRetry(fieldId);
        }, delay);
      } else {
        // Permanent failure
        const failedOp = q.inFlight;
        q.lastError = err;
        q.inFlight = null;
        this._setStatus(fieldId, 'error');
        this._options.onPermanentFailure?.(fieldId, failedOp, err);

        // If it was a create that failed, discard any pending ops (field never existed on server)
        if (failedOp.type === 'create') {
          q.pending = null;
          this._queues.delete(fieldId);
        }
      }
    }
  }

  async _executeOperation(fieldId, operation) {
    const resolvedId = this._resolveId(fieldId);

    switch (operation.type) {
      case 'create': {
        const result = await this._apiAdapter.create(operation.data);
        const serverId = result?.id || result?.data?.id;
        if (serverId && serverId !== fieldId) {
          this._idMap.set(fieldId, serverId);
          // Re-key the queue under the new ID
          const q = this._queues.get(fieldId);
          if (q) {
            this._queues.delete(fieldId);
            this._queues.set(serverId, q);
          }
          this._options.onIdSwap?.(fieldId, serverId);
        }
        break;
      }
      case 'update': {
        await this._apiAdapter.update(resolvedId, operation.data);
        break;
      }
      case 'delete': {
        await this._apiAdapter.delete(resolvedId);
        // Clean up the queue entirely
        this._queues.delete(resolvedId);
        if (resolvedId !== fieldId) this._queues.delete(fieldId);
        break;
      }
    }
  }

  _isRetryable(error) {
    // Network errors (no response) are retryable
    if (!error.response) return true;
    const status = error.response.status;
    // 5xx server errors, 408 timeout, 429 rate limit
    return status >= 500 || status === 408 || status === 429;
  }
}
